import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLazyGetCarrierCodeListQuery } from "../../store/apiSlices/containerTracking";
import { ICellEditorParams } from "ag-grid-community";
import { debounce } from "../../utils";
import { SpinnerIcon } from "../Icons";

export default function LogisticsProviderCellEditor(props: ICellEditorParams) {
  const { value } = props;
  const [trigger, { data, isLoading, isFetching }] =
    useLazyGetCarrierCodeListQuery();
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);

  //   console.log(props);

  const debouncedHandleChange = useCallback(
    debounce((newValue: string) => {
      if (newValue) {
        setOpen(true);
        trigger({ search_query: newValue });
      } else {
        setOpen(false);
      }
    }, 500),
    [trigger]
  );

  // Close dropdown on outside click or Escape
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }

    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const dropdown =
    open && inputRef.current
      ? createPortal(
          <ul
            className="absolute text-sm bg-white border border-gray-300 mt-1 max-h-40 overflow-y-auto z-[2000] shadow-md rounded-md"
            style={{
              top:
                inputRef.current.getBoundingClientRect().bottom +
                window.scrollY,
              left:
                inputRef.current.getBoundingClientRect().left + window.scrollX,
              width: inputRef.current.offsetWidth,
            }}
          >
            {isLoading || isFetching ? (
              <li className="px-2 py-1 flex items-center gap-1">
                <SpinnerIcon className="size-3 text-gray-200 animate-spin fill-blue-600" />
                loading..
              </li>
            ) : data && data.length > 0 ? (
              data.map((c) => (
                <li
                  key={c.Carrier_Name}
                  className="px-2 py-1 cursor-pointer hover:bg-gray-100"
                  onMouseDown={() => {
                    props.node.setDataValue(
                      props.colDef?.field!,
                      `${c.Carrier_Name} (${c.Carrier_Code})`
                    );
                    setOpen(false); // close after selection
                  }}
                >
                  {`${c.Carrier_Name} (${c.Carrier_Code})`}
                </li>
              ))
            ) : (
              <p className="px-2 py-1">No data</p>
            )}
          </ul>,
          document.body
        )
      : null;

  return (
    <>
      <input
        type="search"
        ref={inputRef}
        defaultValue={value as string}
        onChange={(e) => debouncedHandleChange(e.target.value)}
        placeholder="Search carrier..."
        className="w-full border px-2 focus:outline-none border-none"
        autoFocus
      />
      {dropdown}
    </>
  );
}
